import type { jsPDF } from 'jspdf'
import type { CartoucheData, TranslateFn, MireInfo } from './report-types'
import {
  PAGE_W, PAGE_H, MARGIN_L, MARGIN_R,
  CONTENT_W, QR_SIZE, fmtNum, loadImageAsDataUrl,
  ACCENT, GRAY_200, GRAY_400, GRAY_500, GRAY_700, GRAY_900,
  drawMetricBox,
} from './report-helpers'

export async function drawCoverPage(
  doc: jsPDF,
  opts: {
    projectName: string
    cartouche: CartoucheData
    t: TranslateFn
    qrDataUrl: string | null
    mires: MireInfo[]
    totalProcessors: number
    totalPanels: number
    grandTotalPixels: number
    totalConsumption: number
    totalCables: number
  },
): Promise<void> {
  const {
    projectName, cartouche, t, qrDataUrl,
    mires, totalProcessors, totalPanels,
    grandTotalPixels, totalConsumption, totalCables,
  } = opts

  // ── Top accent bar ──────────────────────────────────────────────
  doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b)
  doc.rect(0, 0, PAGE_W, 40, 'F')

  // Logo in accent bar (left)
  try {
    const logoDataUrl = await loadImageAsDataUrl('/icon_full_res.png')
    doc.addImage(logoDataUrl, 'PNG', MARGIN_L, 7, 26, 26)
  } catch { /* skip */ }

  // Title in accent bar
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(20)
  doc.setTextColor(255, 255, 255)
  const titleX = MARGIN_L + 32
  doc.text(projectName || t('report.untitledProject'), titleX, 20, { maxWidth: CONTENT_W - 32 - QR_SIZE - 5 })

  // Subtitle
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  doc.setTextColor(219, 234, 254) // blue-100
  doc.text(t('report.technicalReport'), titleX, 30)

  // QR code in accent bar (right)
  if (qrDataUrl) {
    doc.addImage(qrDataUrl, 'PNG', PAGE_W - MARGIN_R - QR_SIZE + 2, 6, QR_SIZE, QR_SIZE)
  }

  // ── Cartouche info section ──────────────────────────────────────
  let y = 52
  const infoLines: [string, string][] = []
  if (cartouche.client) infoLines.push([t('report.client'), cartouche.client])
  if (cartouche.evenement) infoLines.push([t('report.event'), cartouche.evenement])
  if (cartouche.lieu) infoLines.push([t('report.location'), cartouche.lieu])
  if (cartouche.agence) infoLines.push([t('report.agency'), cartouche.agence])
  if (cartouche.suite) infoLines.push([t('report.suite'), cartouche.suite])
  if (cartouche.quoi) infoLines.push([t('report.subject'), cartouche.quoi])
  if (cartouche.date) infoLines.push([t('report.date'), cartouche.date])
  if (cartouche.de) infoLines.push([t('report.author'), cartouche.de])

  if (infoLines.length > 0) {
    // Two-column layout for info
    const midX = MARGIN_L + CONTENT_W / 2 + 5
    const leftLines = infoLines.slice(0, Math.ceil(infoLines.length / 2))
    const rightLines = infoLines.slice(Math.ceil(infoLines.length / 2))

    const drawInfoCol = (lines: [string, string][], startX: number, startY: number) => {
      let cy = startY
      for (const [label, value] of lines) {
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(7.5)
        doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
        doc.text(label.toUpperCase(), startX, cy)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(9)
        doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
        doc.text(value, startX, cy + 5)
        cy += 14
      }
      return cy
    }

    drawInfoCol(leftLines, MARGIN_L, y)
    drawInfoCol(rightLines, midX, y)
    y = y + Math.max(leftLines.length, rightLines.length) * 14 + 4
  }

  // ── Separator ────────────────────────────────────────────────────
  doc.setDrawColor(GRAY_200.r, GRAY_200.g, GRAY_200.b)
  doc.setLineWidth(0.3)
  doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y)
  y += 8

  // ── Section label ────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(GRAY_700.r, GRAY_700.g, GRAY_700.b)
  doc.text(t('report.projectSummary').toUpperCase(), MARGIN_L, y)
  y += 8

  // ── Metric boxes (3x2 grid) ─────────────────────────────────────
  const boxW = (CONTENT_W - 10) / 3
  const boxH = 28
  const gap = 5

  const metrics: [string, string, boolean][] = [
    [t('report.ledWalls'), String(mires.length), true],
    [t('report.totalProcessors'), String(totalProcessors), false],
    [t('report.totalPanels'), fmtNum(totalPanels), false],
    [t('report.totalPixels'), fmtNum(grandTotalPixels) + ' px', true],
    [t('report.totalPowerShort'), fmtNum(totalConsumption) + ' W', false],
    [t('report.totalCables'), String(totalCables), false],
  ]

  for (let i = 0; i < metrics.length; i++) {
    const col = i % 3
    const row = Math.floor(i / 3)
    const bx = MARGIN_L + col * (boxW + gap)
    const by = y + row * (boxH + gap)
    const [label, value, accent] = metrics[i]
    drawMetricBox(doc, bx, by, boxW, boxH, label, value, accent)
  }

  y += 2 * (boxH + gap) + 10

  // ── Per-wall summary table ──────────────────────────────────────
  if (mires.length > 0 && mires.length <= 8) {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    doc.setTextColor(GRAY_700.r, GRAY_700.g, GRAY_700.b)
    doc.text(t('report.ledWallOverview').toUpperCase(), MARGIN_L, y)
    y += 5

    // Mini table
    const cols = [MARGIN_L, MARGIN_L + 50, MARGIN_L + 90, MARGIN_L + 130]
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(6.5)
    doc.setTextColor(GRAY_400.r, GRAY_400.g, GRAY_400.b)
    doc.text(t('report.ledWall'), cols[0], y)
    doc.text(t('report.resolution'), cols[1], y)
    doc.text(t('report.panelType'), cols[2], y)
    doc.text(t('report.totalPixels'), cols[3], y, { align: 'right' })
    y += 2
    doc.setDrawColor(GRAY_200.r, GRAY_200.g, GRAY_200.b)
    doc.setLineWidth(0.15)
    doc.line(MARGIN_L, y, MARGIN_L + 130, y)
    y += 4

    for (const m of mires) {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(7)
      doc.setTextColor(GRAY_900.r, GRAY_900.g, GRAY_900.b)
      doc.text(m.name, cols[0], y)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(GRAY_700.r, GRAY_700.g, GRAY_700.b)
      doc.text(`${m.totalPixels.width} x ${m.totalPixels.height}`, cols[1], y)
      doc.text(m.linkedPanel ? `${m.linkedPanel.brand} ${m.linkedPanel.name}` : '-', cols[2], y)
      doc.setTextColor(GRAY_500.r, GRAY_500.g, GRAY_500.b)
      doc.text(fmtNum(m.totalPixels.width * m.totalPixels.height) + ' px', cols[3], y, { align: 'right' })
      y += 5.5
    }
  }

  // ── Footer on cover ─────────────────────────────────────────────
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6.5)
  doc.setTextColor(GRAY_400.r, GRAY_400.g, GRAY_400.b)
  doc.text(
    `${t('report.generatedBy')} VisualGrid -- ${new Date().toLocaleDateString()}`,
    PAGE_W / 2, PAGE_H - 12, { align: 'center' },
  )
}
